import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { Elements, CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { stripe as stripePromise, appearance } from '@/utils/stripe'
import { UPDATE_PAYMENT_METHOD } from '@/graphql/subscription-queries'
import { Button } from '@/components/ui/button'

const cardElementOptions = {
    hidePostalCode: true,
    style: {
        base: {
            fontSize: '15px',
            fontFamily: 'Poppins, sans-serif',
            color: '#FFFFFF',
            '::placeholder': { color: '#97AFCD' },
            iconColor: '#97AFCD',
        },
        invalid: { color: '#E57373' },
    },
}

const AddCardInlineInner = ({ onSaved, title, description }) => {
    const stripe = useStripe()
    const elements = useElements()

    const [processing, setProcessing] = useState(false)
    const [error, setError] = useState(null)

    const [updatePaymentMethod] = useMutation(UPDATE_PAYMENT_METHOD, {
        onCompleted: (res) => {
            setProcessing(false)
            setError(null)
            onSaved?.(res?.updatePaymentMethod)
        },
        onError: ({ message }) => {
            setProcessing(false)
            setError(message || 'Failed to save card. Please try again.')
        },
    })

    const handleSubmit = async (event) => {
        event.preventDefault()
        if (!stripe || !elements) return
        setProcessing(true)
        setError(null)

        try {
            const cardElement = elements.getElement(CardElement)
            const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
                type: 'card',
                card: cardElement,
            })

            if (stripeError) {
                setError(stripeError.message)
                setProcessing(false)
                return
            }

            await updatePaymentMethod({
                variables: {
                    paymentMethodInput: {
                        id: paymentMethod.id,
                        cardBrand: paymentMethod.card.brand,
                        cardExpMonth: paymentMethod.card.exp_month,
                        cardExpYear: paymentMethod.card.exp_year,
                        cardLastDigits: paymentMethod.card.last4,
                    },
                },
            })
        } catch (err) {
            setError(err?.message || 'Unexpected error while saving card.')
            setProcessing(false)
        }
    }

    return (
        <form
            onSubmit={handleSubmit}
            className="tw:flex tw:flex-col tw:gap-3 tw:p-4 tw:rounded-[8px] tw:bg-[#08172E] tw:border tw:border-[#24486F]"
        >
            <div>
                <p className="tw:text-white tw:font-semibold tw:text-sm">
                    {title || 'Add a payment method'}
                </p>
                {description && (
                    <p className="tw:text-[12px] tw:text-[#97AFCD] tw:mt-1">{description}</p>
                )}
            </div>
            <div className="tw:bg-[#0C2243] tw:rounded-[6px] tw:px-3 tw:py-3 tw:border tw:border-[#1E3B64]">
                <CardElement options={cardElementOptions} />
            </div>
            {error && <p className="tw:text-[#E57373] tw:text-xs">{error}</p>}
            <div>
                <Button type="submit" disabled={!stripe || processing}>
                    {processing ? 'Saving…' : 'Save card'}
                </Button>
            </div>
        </form>
    )
}

const AddCardInline = (props) => (
    <Elements stripe={stripePromise} options={{ appearance }}>
        <AddCardInlineInner {...props} />
    </Elements>
)

export default AddCardInline
